import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../utils/useAuth"; // adjust path to your auth hook
import { FiTrash2, FiUser, FiRefreshCw, FiSearch } from "react-icons/fi";
import CustomerBot from "../../website/bot/Customerbot";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.dhoorbin.com";

function lastText(raw) {
  if (!raw) return "";
  try {
    const v = JSON.parse(raw);
    if (v?.tool) return "[hotel suggestions]";
    if (v?.invoiceId) return "[booking request]";
    return typeof v === "string" ? v : "[attachment]";
  } catch {
    return raw;
  }
}

export default function AdminChatInbox() {
  const { user } = useAuth();
  const adminId = user?.id;

  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const loadSessions = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/bot/admin/sessions`)
      .then((r) => r.json())
      .then((d) => setSessions(d.sessions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, 8000); // light polling for the list
    return () => clearInterval(t);
  }, [loadSessions]);

  const handleDelete = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this entire chat? This cannot be undone."))
      return;
    try {
      await fetch(`${API_BASE}/bot/session/${sessionId}`, { method: "DELETE" });
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      if (active?.sessionId === sessionId) setActive(null);
    } catch (_) {}
  };

  const filtered = sessions.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [s.fullName, s.email, s.phoneNumber, s.sessionId]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  return (
    <div className="flex h-[calc(100lvh-120px)] border border-gray-200 rounded-2xl overflow-hidden bg-white">
      {/* LEFT: session list */}
      <aside className="w-full sm:w-80 shrink-0 border-r border-gray-200 flex flex-col">
        <div className="p-3 border-b border-gray-200 space-y-2">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-sm text-gray-800">Conversations</p>
            <button
              onClick={loadSessions}
              className={`text-gray-400 hover:text-gray-600 ${loading ? "animate-spin" : ""}`}
            >
              <FiRefreshCw />
            </button>
          </div>
          <div className="relative">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, email"
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <p className="text-center text-xs text-gray-400 mt-8">
              No conversations
            </p>
          )}
          {filtered.map((s) => (
            <div
              key={s.sessionId}
              onClick={() => setActive(s)}
              className={`group flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-gray-100 ${
                active?.sessionId === s.sessionId
                  ? "bg-blue-50"
                  : "hover:bg-gray-50"
              }`}
            >
              <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                <FiUser />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {s.fullName || s.phoneNumber || "Guest"}
                  </p>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                      s.aiEnabled
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {s.aiEnabled ? "AI" : "Human"}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate">
                  {lastText(s.lastMessage) || s.email || s.phoneNumber}
                </p>
              </div>
              <button
                onClick={(e) => handleDelete(s.sessionId, e)}
                className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 shrink-0"
              >
                <FiTrash2 />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* RIGHT: active chat */}
      <main className="flex-1 min-w-0 flex flex-col bg-gray-50">
        {active ? (
          <>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">
                  {active.fullName || "Guest"}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {[active.phoneNumber, active.email]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <button
                onClick={(e) => handleDelete(active.sessionId, e)}
                className="inline-flex items-center gap-1.5 text-xs text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg"
              >
                <FiTrash2 /> Delete
              </button>
            </div>
            <div className="flex-1 min-h-0 p-3">
              <CustomerBot
                key={active.sessionId}
                isAdmin
                adminId={adminId}
                sessionId={active.sessionId}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
            Select a conversation to view
          </div>
        )}
      </main>
    </div>
  );
}
